import type React from 'react';
import type { FilePreviewType } from './fileTypes';

export interface FileUploader {
    id: string;
    name: string;
    email: string;
}

export interface Asset {
    name: string;
    assetId: number | string;
    fileType: string;
    publicUrl: string;
    createdAt?: string;
    source: 'monday' | 'gcs';
    patientFileId?: string;
    /** Only populated for `source === 'gcs'`. Hosts that don't surface the
     *  uploader simply leave it undefined (see FilesCapabilities.showUploadedBy). */
    uploadedBy?: FileUploader | null;
    /** Signed thumbnail read URL for GCS image files; null/undefined otherwise.
     *  May 404 for not-yet-backfilled images - see useThumbnails' fallback. */
    thumbUrl?: string | null;
    /** Coarse storage category (PatientFileType, e.g. CBCT, STL) - GCS assets only. */
    storageCategory?: string | null;
    /** Free-form metadata from `PatientFile.metadata` (e.g. CBCT manifest/binary refs). */
    metadata?: Record<string, unknown> | null;
    /** `PatientFile.internal` (GCS assets only); missing means not internal. */
    internal?: boolean;
}

export interface FileCategory {
    label: string;
    assets: Asset[];
    icon: React.ReactNode;
    /** Whether this category shows the list view's "Preview" button (see
     *  FileCategoryConfig.previewable / useFilesManager's handlePreviewCategory). */
    previewable: boolean;
}

/**
 * Host-swappable "By category" grouping for the list view. Defaults to the
 * built-in extension/storageCategory taxonomy (Images/Videos/Documents/3D
 * Models/CBCT/Others - see fileTypes.categorizeAssetWithType) when the host
 * doesn't supply one, so existing hosts (patient files) are unaffected.
 */
export interface FileCategoryConfig {
    /** Ordered category labels shown in the "By category" list view. */
    order: readonly string[];
    /** Maps an asset to one of `order`'s labels. */
    categorize: (asset: Asset) => string;
    /** Icon shown next to each category's label; keyed by label. */
    icons: Record<string, React.ReactNode>;
    /**
     * Opts specific category labels into the list view's "Preview" button.
     * Optional and additive: when omitted entirely, or when a given label has
     * no entry here, previewability falls back to the pre-existing
     * `label === 'Images'` check - so a host built before this flag existed
     * (like the patient files page's built-in taxonomy) keeps behaving
     * exactly as it did before, with no caller changes required.
     */
    previewable?: Record<string, boolean>;
}

export type LayoutMode = 'list' | 'grid';
export type ListMode = 'category' | 'date';
export type ViewMode = 'list' | 'preview';
export type PreviewType = FilePreviewType;

export interface FileUploadResult {
    success: boolean;
    error?: string;
    /** Original error stack - preserved so hosts can surface it in error reports. */
    errorStack?: string;
    /** Cloud-file id of the uploaded file (set on success). */
    patientFileId?: string;
    /** Underlying storage file id of the uploaded file (set on success). */
    fileId?: string;
}

export interface UploadFileOptions {
    onProgress?: (progress: number) => void;
    metadata?: Record<string, unknown>;
    /** Coarse storage category (PatientFileType). Defaults to the host's choice
     *  (typically ADDITIONAL). */
    fileType?: string;
}

export interface FileUpdateChanges {
    originalFilename?: string;
    fileType?: string;
    metadata?: Record<string, unknown>;
}

/** 'NOT_STARTED' is the shared-ui mapping for "no job exists" (e.g. a host 404). */
export type CbctProcessingStatus = 'NOT_STARTED' | 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

export interface CbctProcessingStatusResult {
    status: CbctProcessingStatus;
    /** Human-readable failure reason, surfaced when status is FAILED. */
    error?: string;
}

export interface CbctProcessingTriggerOptions {
    /** For password-protected scan archives. */
    password?: string;
}

/** Optional capability: trigger + poll CBCT processing. Absent = unprocessed CBCTs aren't previewable. */
export interface CbctProcessingApi {
    getStatus(asset: Asset): Promise<CbctProcessingStatusResult>;
    trigger(asset: Asset, options?: CbctProcessingTriggerOptions): Promise<void>;
}

/**
 * IO boundary for the files module. Each host implements these five operations
 * over its own axios client - the module itself never talks to the network
 * directly (mirrors the cbct module's ScanStorage seam).
 */
export interface FilesApi {
    /** Signed read URL for a GCS-backed patient file. */
    getDownloadUrl(patientId: string, patientFileId: string): Promise<string>;
    deleteFile(patientId: string, patientFileId: string): Promise<void>;
    updateFile(patientId: string, patientFileId: string, changes: FileUpdateChanges): Promise<void>;
    /** Full upload flow (create → PUT → complete). Must resolve with
     *  `{ success: false }` rather than throwing for per-file failures. */
    uploadFile(
        patientId: string,
        file: File,
        options?: UploadFileOptions
    ): Promise<FileUploadResult>;
    /** Backend proxy fetch for cross-origin (Monday.com) files in ZIP downloads. */
    fetchViaProxy(url: string, filename: string): Promise<Blob>;
    /** Optional - see CbctProcessingApi. Absent = today's behavior. */
    cbctProcessing?: CbctProcessingApi;
    /** Copies an image file into the patient's avatar. Rejects on failure; the host owns
     *  the success/error feedback and avatar cache refresh. Absent = action hidden. */
    setProfilePicture?(patientId: string, patientFileId: string): Promise<void>;
}

export interface UploadErrorContext {
    fileName: string;
    fileSize: number;
    /** Stack from the underlying upload failure, when available. */
    errorStack?: string;
}

export interface FilesCapabilities {
    /** Allow deleting GCS-backed files. Default true. */
    canDelete?: boolean;
    /** Allow renaming GCS-backed files (opens the edit modal). Default true. */
    canRename?: boolean;
    /** Allow changing the coarse category in the edit modal. Default false. */
    canReclassify?: boolean;
    /** Show the toolbar's "Upload" toggle and its drop zone. Default true.
     *  Set false when the host renders its own upload form instead (e.g. one
     *  that collects fields the module's generic batch uploader can't host). */
    canUpload?: boolean;
    /** Open the CBCT viewer read-only (no marking edits). Default false. */
    cbctReadOnly?: boolean;
    /** Show "· By {name}" next to upload dates when the asset carries it. Default true. */
    showUploadedBy?: boolean;
    /** Prev/next navigation (+ ArrowLeft/ArrowRight) across previewable assets.
     *  When false the preview collapses to a single asset. Default true. */
    enablePreviewNavigation?: boolean;
    /** @deprecated Ignored - the multi-STL "View in 3D" flow was removed (STLs
     *  preview one at a time; a dedicated full STL viewer replaces it). Kept
     *  only so hosts that still pass it keep typechecking. */
    enableMultiStlPreview?: boolean;
    /** 'radix' uses the shared Tooltip (host must mount TooltipProvider at its
     *  root); 'native' falls back to title attributes. Default 'radix'. */
    tooltips?: 'radix' | 'native';
    /** "More actions" menu (download, delete, ...) in the image preview toolbar.
     *  Default false, so hosts that don't opt in keep the bare preview. */
    showPreviewActions?: boolean;
    /** Offer "Set as profile picture" in that menu; also needs FilesApi.setProfilePicture. Default false. */
    canSetProfilePicture?: boolean;
    /** Observability seam - called once per failed file upload (Sentry etc.). */
    onUploadError?: (error: unknown, ctx: UploadErrorContext) => void;
}

export type ResolvedFilesCapabilities = Required<
    Omit<FilesCapabilities, 'onUploadError' | 'enableMultiStlPreview'>
> &
    Pick<FilesCapabilities, 'onUploadError'>;

export const DEFAULT_FILES_CAPABILITIES: ResolvedFilesCapabilities = {
    canDelete: true,
    canRename: true,
    canReclassify: false,
    canUpload: true,
    cbctReadOnly: false,
    showUploadedBy: true,
    enablePreviewNavigation: true,
    tooltips: 'radix',
    showPreviewActions: false,
    canSetProfilePicture: false
};

export const resolveFilesCapabilities = (
    capabilities?: FilesCapabilities
): ResolvedFilesCapabilities => ({
    ...DEFAULT_FILES_CAPABILITIES,
    ...capabilities
});

export type CbctExportHandler = (
    png: Blob,
    info: { mode: 'pano' | 'ceph'; filename: string; scanId?: string }
) => void | string | Promise<void | string>;

/** Context handed to the host's renameModalExtras render prop (e.g. the
 *  files-viewer CBCT processing trigger). */
export interface RenameModalExtrasContext {
    asset: Asset;
    /** Currently selected coarse category in the modal (may be unsaved). */
    fileType: string;
    /** Category as stored server-side when the modal opened. */
    initialFileType: string;
    isSaving: boolean;
}
